import { useState } from "react";
import { useSearchParams } from "react-router";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import { usePortalGetAccess } from "@shared-core/domain/usePortalGetAccess";
import { getRuntimeConfig } from "@shared-core/config/runtime-config";
import { Card } from "../../app/components/primitives/card";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";
import { Label } from "../../app/components/primitives/label";
import { MailCheck, ArrowLeft, AlertCircle } from "lucide-react";
import { PortalSignInShell } from "./signin/PortalSignInShell";
import { PortalReviewBar } from "./signin/PortalReviewBar";
import { PortalGetAccessCard } from "./signin/PortalGetAccessCard";

const SIGN_IN_SUBTITLE =
  "Sign in with the email address you already use with us.";

// J0 -- sign in.
//
// The page asks for ONE thing: the email address. "Continue" POSTs it (form field `login_hint`) to
// /customer-api/auth/initiate, which answers 303 to Entra External ID's hosted page, opened at its
// password step with the address filled in (apps/api/app/auth/customer_signin.py). A POST body, not a
// query string, so the email never lands in an access log, a proxy error log or a trace.
// The password is typed THERE and only there: a server-side authorization-code exchange (04_ §9's
// httpOnly-cookie design) structurally needs Entra to host the credential prompt, so a password box
// on this page could never take part in the exchange -- it only made customers type it twice.
// Entra's own password step also carries the forgotten-password path, so this page words it as a
// pointer, not a link or field of ours.
//
// A native form submit, not react-router: this leaves the SPA entirely for an API route, as a
// full-page OAuth redirect requires.
//
// "Get access" (docs/features/customer-storefront, P3, decided): for someone with no account, an
// email box that asks for a setup link, posted to POST /customer-api/auth/get-access through the
// data adapter. The answer is the same whether or not the address is recognised, and the page never
// words it: it renders the backend's message, also when the request fails to reach us (a failure
// that depends on nothing about the address must not be told apart from the answer to a stranger).
// Being told to slow down (429, with when to try again) and a string that is not an address (the
// backend's field-tagged 400, next to the input) are the person's to act on and are shown.
//
// Offered only when GET /customer-api/auth/options says it is on. Off, or when that read fails, the
// page promises no email and the footer text says how to find the invitation.
//
// Existing Brand Hub users sign in with the email address they already have registered; their Brand
// Hub password is NOT carried over (brief §14a). The "email me a link instead" toggle was dropped
// (decided 2026-08-27): Entra External ID offers one local-account sign-in method per user flow.

/**
 * The sign-in page. In a testing preview (`customerAuthMode: "simulated"`, quote-to-invoice-e2e-testing 07 §2)
 * there is no External ID to sign in with: a tester signs in as a customer from the test console, so the page says
 * that instead. `customerAuthMode` is the runtime configuration's unless a story passes one.
 */
export function PortalSignInPage({
  customerAuthMode,
}: { customerAuthMode?: string } = {}) {
  const mode = customerAuthMode ?? getRuntimeConfig().customerAuthMode;
  return mode === "simulated" ? (
    <PortalSimulatedSignIn />
  ) : (
    <PortalExternalIdSignIn />
  );
}

/** A testing preview: sign in from the test console (its "Sign in as customer" link opens this portal signed in). */
function PortalSimulatedSignIn() {
  return (
    <PortalSignInShell
      title="Your orders and spend"
      subtitle="This is a testing preview."
    >
      <Card className="p-5">
        <div className="space-y-2 text-center">
          <p className="font-medium">Sign in from the test console</p>
          <p className="text-sm text-muted-foreground">
            Open the test console in the staff app, pick a customer person (from
            a scenario, or any customer in this preview) and choose “Sign in as
            customer”. The link signs this browser in once, within a minute.
          </p>
        </div>
      </Card>
    </PortalSignInShell>
  );
}

function PortalExternalIdSignIn() {
  const [email, setEmail] = useState("");
  const [accessEmail, setAccessEmail] = useState("");
  const [searchParams] = useSearchParams();
  const getAccess = usePortalGetAccess(useCustomerDataAdapter());
  const { options, request } = getAccess;
  const signInError = searchParams.get("error");
  const isOffered = options.status === "offered";
  const isSending = request.status === "submitting";

  if (request.status === "sent") {
    return (
      <PortalSignInShell
        title="Your orders and spend"
        subtitle={SIGN_IN_SUBTITLE}
        footer={<PortalReviewBar />}
      >
        <Card className="p-5">
          <div className="space-y-3 text-center">
            <MailCheck className="mx-auto size-7 text-primary" />
            <p className="font-medium">Check your email</p>
            {/* The backend's message: the same whether or not the address exists. */}
            <p className="text-sm text-muted-foreground">{request.message}</p>
            <Button
              variant="ghost"
              onClick={getAccess.reset}
              className="text-sm"
            >
              <ArrowLeft className="size-4" />
              Back to sign in
            </Button>
          </div>
        </Card>
      </PortalSignInShell>
    );
  }

  return (
    <PortalSignInShell
      title="Your orders and spend"
      subtitle={SIGN_IN_SUBTITLE}
      footer={<PortalReviewBar />}
    >
      {signInError && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <span>
            {signInError === "denied"
              ? "Sign-in was cancelled. Try again when you're ready."
              : "Something went wrong signing you in. Please try again."}
          </span>
        </div>
      )}
      <Card className="p-5">
        {/* A native POST, not a redirect with the email in the URL: a URL reaches access logs,
            proxy error logs and traces, a request body does not. The server answers 303 to Entra. */}
        <form
          method="post"
          action="/customer-api/auth/initiate"
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="portal-email">Email address</Label>
            <Input
              id="portal-email"
              name="login_hint"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourcompany.co.uk"
            />
          </div>

          <Button type="submit" className="w-full">
            Continue
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            You&apos;ll enter your password on the next step. Forgotten it? You
            can reset it there.
          </p>
        </form>
      </Card>

      {isOffered ? (
        <PortalGetAccessCard
          email={accessEmail}
          onEmailChange={(value) => {
            setAccessEmail(value);
            getAccess.reset();
          }}
          onSubmit={() => void getAccess.submit(accessEmail)}
          request={request}
          isSending={isSending}
          isBusy={isSending}
        />
      ) : options.status === "not_offered" ? (
        <p className="text-center text-xs text-muted-foreground">
          First time here? Look for the invitation email we sent to your Brand
          Hub address — it lets you set a password. If you can&apos;t find it,
          call us on 01752 663111.
        </p>
      ) : null}
    </PortalSignInShell>
  );
}
